/**
 * Les FORMATS À SAISIR d'une variante d'item national (10/10/2026, décision du prof : un interrupteur
 * « QCM / Saisie » dans le QCM de suivi, QCM par défaut).
 *
 * Une variante à saisir part d'un tirage QCM de l'item : même énoncé, mêmes valeurs, mêmes figures ; seuls
 * les choix disparaissent. Deux formes :
 *   - la SAISIE : la bonne réponse est un nombre, l'élève le tape ; son unité (€, cm, %, °…) s'affiche après
 *     le champ. Tolérance numérique (4,4 = 4,40 ; 79009 = 79 009), comme dans nos exerciseurs ;
 *   - le TEXTE À TROUS : une fraction (\dfrac à deux trous, en KaTeX) ou une durée (« … h … min »), qu'un
 *     seul champ lirait mal.
 * Un item n'a un format à saisir que si sa réponse est UNIQUE et ne s'écrit que d'une façon. Restent en QCM :
 * les énoncés qui renvoient aux choix (« le plus petit parmi… », « un multiple de 50 », « un nombre entre
 * 6,522 et 6,523 »), les phrases, les figures, les encadrements, les expressions, et les fractions qu'une
 * fraction égale rendrait aussi justes (2/5 pour 4/10 : le texte à trous la compterait fausse).
 * Une saisie est plus exigeante qu'un QCM (rien à reconnaître) : à connaissances égales, la réussite y est
 * plus basse qu'en septembre, d'où le QCM par défaut.
 */
import { makeSaisie, makeTexteTrou } from '../utils';

/** Par module (niveau|type_label) : les items en saisie, les items à trous ; tous les autres restent en QCM. */
export const FORMATS_A_SAISIR = {
    '6|Automatisme': {
        saisie: ['Q01', 'Q02', 'Q03', 'Q04', 'Q06', 'Q07', 'Q12', 'Q13', 'Q14', 'Q15', 'Q17', 'Q19', 'Q20', 'Q21'],
        trous: ['Q11'],
    },
    '6|Résolution de problèmes': {
        saisie: ['Q02', 'Q03', 'Q04', 'Q05', 'Q06', 'Q07', 'Q08', 'Q09', 'Q10', 'Q11', 'Q12', 'Q13', 'Q14', 'Q16', 'Q17', 'Q18'],
        trous: ['Q19'],
    },
    '5|Automatisme': {
        saisie: ['Q02', 'Q04', 'Q05', 'Q07', 'Q08', 'Q10', 'Q11', 'Q12', 'Q13', 'Q15', 'Q16', 'Q19', 'Q21', 'Q23', 'Q26', 'Q29', 'Q30'],
        trous: ['Q06', 'Q27'],
    },
    '4|Automatisme': {
        saisie: ['Q02', 'Q04', 'Q05', 'Q08', 'Q09', 'Q10', 'Q11', 'Q12', 'Q14', 'Q17', 'Q18', 'Q19'],
        trous: ['Q03', 'Q07', 'Q15'],
    },
    '4|Résolution de problèmes': {
        saisie: ['Q01', 'Q02', 'Q03', 'Q04', 'Q05', 'Q06', 'Q07', 'Q09', 'Q11', 'Q12', 'Q14', 'Q16', 'Q17', 'Q18'],
        trous: [],
    },
};

/** Le format à saisir d'un item : 'saisie', 'trous', ou null (l'item reste en QCM). */
export const formatDeSaisie = (niveau, typeLabel, questionNum) => {
    const formats = FORMATS_A_SAISIR[`${niveau}|${typeLabel}`];
    if (!formats) return null;
    if (formats.saisie.includes(questionNum)) return 'saisie';
    if (formats.trous.includes(questionNum)) return 'trous';
    return null;
};

const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F'];

// Filet : un énoncé qui renvoie aux choix n'a pas de réponse unique à taper (la liste ci-dessus l'écarte déjà).
const RENVOIE_AUX_CHOIX = /\bparmi\b|\bsuivants\b|\bchoisir\b|\blequel\b|\blaquelle\b|propositions/i;
// Un nombre (espaces de milliers, virgule, signe moins), puis son unité éventuelle, sans chiffre :
// « 4,60 € », « 1 330 L », « 100° », « 104 pages ».
const NOMBRE_ET_UNITE = /^(-?\d{1,3}(?: \d{3})+(?:,\d+)?|-?\d+(?:,\d+)?)\s*([^\d\s][^\d]*?)?\s*\.?$/;
// « $\dfrac{3}{8}$ », suivi ou non d'un texte (« de la figure. »)
const FRACTION = /^\$\\[dt]?frac\{(\d+)\}\{(\d+)\}\$\s*(.*)$/;
// « 10 h 55 », « 2 h 23 min »
const DUREE = /^(\d+)\s*h\s*(\d+)(\s*min)?$/;

const bonneReponseDuQcm = (qcm) => {
    const choix = qcm?.contenu?.choix || [];
    const juste = choix[LETTRES.indexOf(qcm?.contenu?.bonne_reponse)];
    if (typeof juste !== 'string') throw new Error('Format à saisir : bonne réponse du QCM introuvable.');
    return juste;
};

/** Le texte nu d'une réponse : KaTeX simple retiré, vrai signe moins et espaces fines ramenés au clavier. */
const texteNu = (s) => s
    .replace(/\$/g, '')
    .replace(/\\mathrm\{\}/g, '')
    .replace(/\{,\}/g, ',')
    .replace(/−/g, '-')
    .replace(/[  ]/g, ' ')
    .trim();

const optionsDe = (qcm) => ({
    niveau: qcm.niveau,
    matiere: qcm.matiere,
    attendu: qcm.attendu,
    label: qcm._meta?.label,
    elements_additionnels: qcm.elements_additionnels,
});

/** La variante en SAISIE d'un tirage QCM : même énoncé, la bonne réponse à taper, son unité après le champ. */
export const versSaisie = (qcm) => {
    if (RENVOIE_AUX_CHOIX.test(String(qcm?.texte || '').replace(/<[^>]+>/g, ' '))) {
        throw new Error('Format à saisir : l\'énoncé renvoie aux choix.');
    }
    const m = NOMBRE_ET_UNITE.exec(texteNu(bonneReponseDuQcm(qcm)));
    if (!m) throw new Error('Format à saisir : la bonne réponse n\'est pas un nombre.');
    const saisie = makeSaisie(m[1], qcm.texte, { ...optionsDe(qcm), suffixe: (m[2] || '').trim() });
    return { ...saisie, _meta: { ...qcm._meta } };
};

/**
 * La variante en TEXTE À TROUS d'un tirage QCM : une fraction en \dfrac à deux trous ({{{3}}}, la convention
 * des trous dans un groupe KaTeX), ou une durée « {{2}} h {{23}} min ». Les trous ne s'échangent pas : un
 * numérateur n'est pas un dénominateur, des heures ne sont pas des minutes.
 */
export const versTrous = (qcm) => {
    const juste = bonneReponseDuQcm(qcm).trim();
    let phrase = null;
    const fraction = FRACTION.exec(juste);
    if (fraction) {
        const suite = fraction[3] ? `\\text{ ${fraction[3]}}` : '';
        phrase = '$\\dfrac{{{' + fraction[1] + '}}}{{{' + fraction[2] + '}}}' + suite + '$';
    }
    const duree = phrase ? null : DUREE.exec(texteNu(juste));
    if (duree) phrase = `{{${duree[1]}}} h {{${duree[2]}}}${duree[3] ? ' min' : ''}`;
    if (!phrase) throw new Error('Format à saisir : la bonne réponse n\'est ni une fraction ni une durée.');
    const trous = makeTexteTrou(phrase, qcm.texte, { ...optionsDe(qcm), mode: 'saisie', strict: false });
    return { ...trous, _meta: { ...qcm._meta } };
};

/** La conversion d'un tirage QCM vers un format à saisir. */
export const VERS_FORMAT = { saisie: versSaisie, trous: versTrous };
